import { useCallback, useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import { toaster } from 'components/toaster/toaster';
import { useCart } from 'hooks/use-cart';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LoyaltyAppliedOffer, LoyaltyOffer } from 'state/loyalty/types';
import { isDiscountOffer } from 'state/loyalty/utils';

import { useLoyaltyOfferAvailabilityEvaluation } from './use-loyalty-offer-availability-evaluation';

/**
 * This hook is used to remove an expired offer from the cart and displays a toast notification to the user.
 * Once the offersFeedbackMap is available, it will check if there is an offer with an error without threshold.
 * If there is, it will remove the offer from the cart and display a toast notification to the user.
 */
export const useRemoveExpiredOfferFromCart = () => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const { cartEntries, removeFromCart } = useCart();

  const toastWasDisplayed = useRef(false);

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const appliedLoyaltyDiscountOffer = useAppSelector(
    selectors.loyalty.selectDiscountAppliedCmsOffer
  );
  const offersFeedbackMap = useAppSelector(selectors.loyalty.selectOfferFeedbackMap);
  const { isOfferUnavailable } = useLoyaltyOfferAvailabilityEvaluation();

  const removeOffer = useCallback(
    (offer: LoyaltyAppliedOffer | LoyaltyOffer) => {
      dispatch(actions.loyalty.removeAppliedOffer(offer));
      if ('loyaltyEngineId' in offer && isDiscountOffer(offer)) {
        dispatch(actions.loyalty.resetAppliedDiscountOffers());
      } else if ('cartId' in offer && offer.cartId) {
        removeFromCart({ cartId: offer.cartId });
      }
      toaster.informative(formatMessage({ id: 'removeExpiredOfferItem' }));
      toastWasDisplayed.current = true;
    },
    [dispatch, formatMessage, removeFromCart]
  );

  const handleExpiredOfferRemoval = useCallback(() => {
    if (toastWasDisplayed.current) {
      return;
    }

    const appliedOffer = appliedOffers.find(offer =>
      cartEntries.some(entry => entry.cartId === offer.cartId)
    );

    // Only one selected offer can exist in the cart.
    const offer = appliedOffer || appliedLoyaltyDiscountOffer;

    if (offer && isOfferUnavailable(offer)) {
      removeOffer(offer);
    }
  }, [appliedOffers, appliedLoyaltyDiscountOffer, isOfferUnavailable, cartEntries, removeOffer]);

  useEffect(() => {
    if (offersFeedbackMap) {
      handleExpiredOfferRemoval();
    }
  }, [offersFeedbackMap, handleExpiredOfferRemoval]);
};
